import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiGrid } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const windowStyles = [
  { value: 'single_hung', label: 'Single Hung' },
  { value: 'double_hung', label: 'Double Hung' },
  { value: 'slider', label: 'Slider' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'custom', label: 'Custom' }
];

const DealerWindowSizeManager = () => {
  const { dealerId, userRole } = useAuth();
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    width_inches: '',
    height_inches: '',
    style: 'single_hung'
  });

  useEffect(() => {
    if (dealerId) loadWindows();
  }, [dealerId]);

  const loadWindows = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('dealer_window_sizes')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('width_inches', { ascending: true });
      if (!error && data) setWindows(data);
    } catch (err) {
      console.error('Failed to load window sizes:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildLabel = (w, h, style) => {
    const styleName = windowStyles.find(s => s.value === style)?.label || style;
    return `${w}x${h} ${styleName}`;
  };

  const handleSave = async () => {
    const w = parseFloat(form.width_inches);
    const h = parseFloat(form.height_inches);

    if (!w || w <= 0 || !h || h <= 0) {
      alert('Please enter valid width and height in inches.');
      return;
    }

    const label = buildLabel(w, h, form.style);

    const duplicate = windows.find(win =>
      win.id !== editingWindow?.id && win.label === label
    );
    if (duplicate) {
      alert(`Window "${label}" already exists.`);
      return;
    }

    setSaving(true);
    try {
      if (editingWindow) {
        const { error } = await supabase
          .from('dealer_window_sizes')
          .update({ label, width_inches: w, height_inches: h, style: form.style })
          .eq('id', editingWindow.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('dealer_window_sizes')
          .insert([{
            dealer_id: dealerId,
            label,
            width_inches: w,
            height_inches: h,
            style: form.style,
            is_active: true
          }]);
        if (error) throw error;
      }

      setShowForm(false);
      setEditingWindow(null);
      setForm({ width_inches: '', height_inches: '', style: 'single_hung' });
      await loadWindows();
    } catch (err) {
      console.error('Failed to save window size:', err);
      alert('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (win) => {
    setEditingWindow(win);
    setForm({
      width_inches: win.width_inches.toString(),
      height_inches: win.height_inches.toString(),
      style: win.style
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this window size?')) return;
    try {
      const { error } = await supabase
        .from('dealer_window_sizes')
        .delete()
        .eq('id', id);
      if (!error) setWindows(windows.filter(w => w.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingWindow(null);
    setForm({ width_inches: '', height_inches: '', style: 'single_hung' });
  };

  // Quick-add common window sizes
  const presetWindows = [
    { w: 18, h: 27, s: 'single_hung' },
    { w: 22, h: 36, s: 'single_hung' },
    { w: 24, h: 36, s: 'double_hung' },
    { w: 24, h: 36, s: 'single_hung' },
    { w: 30, h: 36, s: 'double_hung' },
    { w: 36, h: 36, s: 'slider' },
    { w: 36, h: 48, s: 'single_hung' },
    { w: 24, h: 24, s: 'fixed' }
  ];
  const existingLabels = new Set(windows.map(w => w.label));
  const availablePresets = presetWindows.filter(p => !existingLabels.has(buildLabel(p.w, p.h, p.s)));

  const handleQuickAdd = async (preset) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('dealer_window_sizes')
        .insert([{
          dealer_id: dealerId,
          label: buildLabel(preset.w, preset.h, preset.s),
          width_inches: preset.w,
          height_inches: preset.h,
          style: preset.s,
          is_active: true
        }]);
      if (error) throw error;
      await loadWindows();
    } catch (err) {
      console.error('Failed to quick-add:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!dealerId) {
    return (
      <div className="p-6 text-center text-gray-500">
        <FiGrid className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Window size management is available for dealer accounts.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Window Sizes</h1>
          <p className="text-gray-600">Set up your window sizes once. They appear in the window picker on every upload.</p>
        </div>
        <button
          onClick={() => { setEditingWindow(null); setForm({ width_inches: '', height_inches: '', style: 'single_hung' }); setShowForm(true); }}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <FiPlus className="w-5 h-5" />
          Add Window Size
        </button>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingWindow ? 'Edit Window Size' : 'New Custom Window Size'}
              </h3>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Width (inches)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={form.width_inches}
                    onChange={(e) => setForm({ ...form, width_inches: e.target.value })}
                    placeholder="e.g., 24"
                    className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-mono"
                  />
                </div>
                <span className="text-2xl font-bold text-gray-400 pb-2">×</span>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Height (inches)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={form.height_inches}
                    onChange={(e) => setForm({ ...form, height_inches: e.target.value })}
                    placeholder="e.g., 36"
                    className="w-28 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
                  <select
                    value={form.style}
                    onChange={(e) => setForm({ ...form, style: e.target.value })}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    {windowStyles.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-5 py-2 rounded-lg transition-colors"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiCheck className="w-4 h-4" />
                    )}
                    {editingWindow ? 'Update' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Add */}
      {availablePresets.length > 0 && windows.length < 5 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Quick Add Popular Sizes</h4>
          <div className="flex flex-wrap gap-2">
            {availablePresets.slice(0, 8).map((p, i) => (
              <button
                key={i}
                onClick={() => handleQuickAdd(p)}
                disabled={saving}
                className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 text-sm font-mono rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {buildLabel(p.w, p.h, p.s)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Window Sizes Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      ) : windows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <FiGrid className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No custom window sizes yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Add the window sizes you stock. They'll show up in the window picker when you upload sheds.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg transition-colors"
          >
            <FiPlus className="w-5 h-5" />
            Add Your First Window Size
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {windows.map((win) => (
            <motion.div
              key={win.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center hover:shadow-md transition-shadow group relative"
            >
              <div className="text-lg font-bold text-gray-900 font-mono">{win.width_inches}x{win.height_inches}</div>
              <div className="text-xs text-gray-500 mt-1 capitalize">{windowStyles.find(s => s.value === win.style)?.label || win.style}</div>

              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                <button
                  onClick={() => handleEdit(win)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-700"
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(win.id)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-600"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealerWindowSizeManager;
